// src/utils/evidencias.js
//
// Lógica pura da Central de Evidências (Fase 1).
// Sem React, sem Firestore — apenas funções determinísticas e testáveis.

import { extractUrlsFromText } from '../components/common/LinkifiedText';

// ---------------------------------------------------------------------------
// normalizeUrl
// ---------------------------------------------------------------------------
/**
 * Produz a chave canônica de agrupamento para uma URL.
 * - Host em minúsculas
 * - Remove barra final do path
 * - Prefixa https:// em URLs que começam com www.
 * - Preserva path e query (IDs do Drive vivem no path)
 *
 * @param {string} href - URL já com protocolo (http:// ou https://)
 * @returns {string}
 */
export function normalizeUrl(href) {
  if (!href) return '';
  try {
    // Garantir protocolo para o construtor URL
    const withProto = href.startsWith('www.')
      ? `https://${href}`
      : href;
    const url = new URL(withProto);
    // Host em minúsculas, sem prefixo www., + path sem barra final + query (sem hash)
    const rawHost = url.hostname.toLowerCase();
    const host = rawHost.startsWith('www.') ? rawHost.slice(4) : rawHost;
    const path = url.pathname.replace(/\/$/, '') || '';
    const search = url.search || '';
    return `https://${host}${path}${search}`;
  } catch {
    // Fallback simples para URLs malformadas
    return href.toLowerCase().replace(/\/+$/, '');
  }
}

// ---------------------------------------------------------------------------
// classifyUrl
// ---------------------------------------------------------------------------
/**
 * Classifica uma URL para exibição de badge.
 *
 * @param {string} href
 * @returns {'drive'|'gov'|'pdf'|'video'|'web'}
 */
export function classifyUrl(href) {
  if (!href) return 'web';
  const lower = href.toLowerCase();
  try {
    const url = new URL(lower.startsWith('www.') ? `https://${lower}` : lower);
    const host = url.hostname;
    const path = url.pathname;

    if (host.includes('drive.google.com') || host.includes('docs.google.com')) {
      return 'drive';
    }
    if (host.endsWith('.gov.br')) {
      return 'gov';
    }
    if (path.endsWith('.pdf')) {
      return 'pdf';
    }
    if (
      host.includes('youtube.com') ||
      host.includes('youtu.be') ||
      host.includes('vimeo.com')
    ) {
      return 'video';
    }
  } catch {
    // URL malformada → 'web'
  }
  return 'web';
}

// ---------------------------------------------------------------------------
// detectarUrlSessaoTemporaria
// ---------------------------------------------------------------------------
/**
 * Detecta se a URL contém identificadores de sessão temporária (como jsessionid,
 * phpsessid, etc.) que expiram com o tempo e enfraquecem a evidência para auditoria.
 *
 * @param {string} href
 * @returns {boolean}
 */
export function detectarUrlSessaoTemporaria(href) {
  if (!href || typeof href !== 'string') return false;
  return /;?jsessionid=[a-zA-Z0-9_\-.]+/i.test(href) ||
         /[?&](phpsessid|aspsessionid|session_id|sid)=[a-zA-Z0-9_\-.]+/i.test(href);
}

// ---------------------------------------------------------------------------
// isEvidenciaObrigatoria
// ---------------------------------------------------------------------------
/**
 * Replica a regra já usada em RespostaCardPublic.jsx: evidências só são
 * exigidas quando o nível de adoção declarado é "Adota em parte" ou "Adota em
 * grande parte ou totalmente". Nos demais níveis (ex.: "Não adota", "Não se
 * aplica"), a justificativa/evidência é opcional.
 *
 * @param {string} respostaPrincipal
 * @returns {boolean}
 */
export function isEvidenciaObrigatoria(respostaPrincipal) {
  return (
    respostaPrincipal === 'Adota em parte' ||
    respostaPrincipal === 'Adota em grande parte ou totalmente'
  );
}

// ---------------------------------------------------------------------------
// buildEvidenceIndex
// ---------------------------------------------------------------------------
/**
 * Constrói o índice invertido "evidência → questões" a partir das respostas.
 *
 * @param {Array<Object>} respostas  - Documentos da coleção `respostas`.
 * @param {Map<string, Object>} questoesMap  - Map idQuestao → dados da questão.
 * @param {Map<string, string>} usuariosMap  - Map userId → nome do articulador.
 * @returns {{
 *   evidencias: Array<Object>,
 *   semEvidencia: Array<Object>,
 *   kpis: Object
 * }}
 */
/**
 * Constrói o índice invertido "evidência → questões" a partir das respostas e anexos.
 *
 * @param {Array<Object>} respostas  - Documentos da coleção `respostas`.
 * @param {Map<string, Object>} questoesMap  - Map idQuestao → dados da questão.
 * @param {Map<string, string>} usuariosMap  - Map userId → nome do articulador.
 * @param {Array<Object>} [anexosList=[]] - Documentos da coleção `anexos_evidencia`.
 * @returns {{
 *   evidencias: Array<Object>,
 *   semEvidencia: Array<Object>,
 *   kpis: Object
 * }}
 */
export function buildEvidenceIndex(respostas, questoesMap, usuariosMap, anexosList = []) {
  // Mapa canônico: chaveCanonica → objeto evidência
  const evidenciaMap = new Map();

  // Mapeamento de anexos por idResposta
  const anexosPorResposta = new Map();
  for (const anexo of anexosList) {
    if (!anexo.idResposta) continue;
    if (!anexosPorResposta.has(anexo.idResposta)) {
      anexosPorResposta.set(anexo.idResposta, []);
    }
    anexosPorResposta.get(anexo.idResposta).push(anexo);
  }

  // Processar anexos como evidências de primeira classe
  for (const anexo of anexosList) {
    const questao = questoesMap.get(anexo.idQuestao) || {};
    const nomeArticulador = usuariosMap.get(anexo.criadoPor) || anexo.criadoPor || '';
    const key = `anexo_${anexo.id}`;

    evidenciaMap.set(key, {
      url: anexo.nomeArquivo || 'Anexo PDF',
      href: null,
      categoria: 'anexo',
      dominio: 'anexo-interno',
      isAnexo: true,
      anexoData: anexo,
      ocorrencias: [{
        idQuestao: anexo.idQuestao,
        idResposta: anexo.idResposta,
        codigo: questao.codigo || '',
        eixo: questao.eixo || '',
        enunciado: questao.enunciado || '',
        userId: anexo.criadoPor || '',
        nomeArticulador,
        dataUltimaEdicao: anexo.criadoEm || null,
        justificativa: '',
      }],
    });
  }

  // Lista de questões respondidas, SEM link e SEM anexo, cujo nível de adoção EXIGE evidência
  const semEvidencia = [];

  // Contadores para KPIs
  let totalMencoes = 0;
  let questoesComLink = 0;
  let questoesComAnexo = 0;
  let questoesComEvidencia = 0;
  let questoesRespondidas = 0;
  let questoesExigemEvidencia = 0;
  let questoesExigemEvidenciaComLink = 0;

  for (const resposta of respostas) {
    // Só processa respostas com resposta_principal preenchida
    if (!resposta.resposta_principal) continue;

    questoesRespondidas += 1;

    const questao = questoesMap.get(resposta.idQuestao) || {};
    const nomeArticulador =
      usuariosMap.get(resposta.userId) || resposta.userId || '';
    const exigeEvidencia = isEvidenciaObrigatoria(resposta.resposta_principal);
    if (exigeEvidencia) questoesExigemEvidencia += 1;

    const urls = extractUrlsFromText(resposta.justificativa || '');
    const anexosDaResposta = anexosPorResposta.get(resposta.id) || [];
    const temAnexo = anexosDaResposta.length > 0;
    const temLink = urls.length > 0;

    if (temAnexo) questoesComAnexo += 1;
    if (temLink) {
      questoesComLink += 1;
      if (exigeEvidencia) questoesExigemEvidenciaComLink += 1;
    }

    if (temLink || temAnexo) {
      questoesComEvidencia += 1;
    }

    if (!temLink && !temAnexo) {
      // Só entra na Cobertura quando o nível de adoção exige evidência.
      if (exigeEvidencia) {
        semEvidencia.push({
          idQuestao: resposta.idQuestao,
          codigo: questao.codigo || '',
          eixo: questao.eixo || '',
          enunciado: questao.enunciado || '',
          userId: resposta.userId || '',
          nomeArticulador,
          justificativa: resposta.justificativa || '',
          temJustificativa: Boolean(
            resposta.justificativa && resposta.justificativa.trim().length > 0
          ),
        });
      }
      continue;
    }

    for (const { href } of urls) {
      const canonical = normalizeUrl(href);
      totalMencoes += 1;

      if (!evidenciaMap.has(canonical)) {
        const url = new URL(canonical);
        evidenciaMap.set(canonical, {
          url: canonical,
          href: canonical,
          categoria: classifyUrl(canonical),
          dominio: url.hostname.toLowerCase(),
          ocorrencias: [],
        });
      }

      evidenciaMap.get(canonical).ocorrencias.push({
        idQuestao: resposta.idQuestao,
        idResposta: resposta.id || '',
        codigo: questao.codigo || '',
        eixo: questao.eixo || '',
        enunciado: questao.enunciado || '',
        userId: resposta.userId || '',
        nomeArticulador,
        dataUltimaEdicao: resposta.dataUltimaEdicao || null,
        justificativa: resposta.justificativa || '',
      });
    }
  }

  const evidencias = Array.from(evidenciaMap.values());
  const dominiosDistintos = new Set(evidencias.filter(e => !e.isAnexo).map((e) => e.dominio)).size;

  const kpis = {
    totalLinksUnicos: evidencias.filter(e => !e.isAnexo).length,
    totalAnexos: anexosList.length,
    totalMencoes,
    questoesComLink,
    questoesComAnexo,
    questoesComEvidencia,
    questoesRespondidas,
    questoesExigemEvidencia,
    questoesExigemEvidenciaComLink,
    dominiosDistintos,
  };

  return { evidencias, semEvidencia, kpis };
}
